import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaEdit, FaTrash } from "react-icons/fa";
import { saveJsonToBack, fetchJsonFromBack } from "../../../store/jsonSlice";
import Sortable from "sortablejs";
import Title from "../../../components/Title";
import TextInput from "../../../components/TextInput";
import Button from "../../../components/Button";
import TextArea from "../../../components/TextArea";

const StagesManage = () => {
  const dispatch = useDispatch();
  const { folderName, filename } = useParams();
  const { jsonData, loading: jsonLoading } = useSelector((state) => state.json);

  // Extraire le chapitre et le sous-dossier du folderName
  /* const [chapter, subFolder] = folderName ? folderName.split('/') : []; */

  // Initialiser avec un objet process vide si non défini
  const process = jsonData?.process || { stages: [] };
  const stages = Array.isArray(process.stages) ? process.stages : [];

  const [editingIndex, setEditingIndex] = useState(null);
  const [message, setMessage] = useState(null);
  const [stageData, setStageData] = useState({
    stageID: "",
    stageTitle: "",
    stageDesc: "",
    stageRequiresStageID: "",
    stagePath: ""
  });
  // pathSteps contient les IDs sélectionnés dans l'ordre
  const [pathSteps, setPathSteps] = useState([]);

  const steps = useSelector(state =>
    Array.isArray(state.json?.jsonData?.process?.steps)
      ? state.json.jsonData.process.steps
      : []
  );
  // Pour l'affichage à gauche :
  const availableSteps = steps.map(s => ({ id: s.stepID, title: s.stepTitle || 'Sans titre' }));
  // Clé calculée à partir des IDs disponibles : quand elle change, React démonte
  // complètement les conteneurs Sortable (cleanup useEffect = destroy), puis les
  // remonte proprement, ce qui élimine tout conflit entre Sortable et le DOM React.
  const sortableContainerKey = availableSteps.map(s => s.id).join('-') || 'empty';
  const leftRef = useRef(null);
  const rightRef = useRef(null);

  // Supprime un step du chemin du stage (pathSteps)
  function handleRemoveStepFromPath(stepId) {
    setPathSteps(prev => prev.filter(id => id !== stepId));
    setStageData(prev => ({
      ...prev,
      stagePath: prev.stagePath
        ? prev.stagePath.split('.').filter(id => Number(id) !== stepId).join('.')
        : ''
    }));
  }
  // Met à jour le champ stagePath à chaque changement de pathSteps
  useEffect(() => {
    setStageData(prev => ({
      ...prev,
      stagePath: pathSteps.length > 0 ? pathSteps.join('.') : ''
    }));
  }, [pathSteps]);

  // Chargement initial
  useEffect(() => {
    const loadData = async () => {
      if (folderName && filename && !jsonLoading) {
        console.log('Chargement du fichier:', { folderName, filename });

        try {
          await dispatch(
            fetchJsonFromBack({
              folderName,
              filename: filename.replace(/\.json$/, ''), // Supprimer l'extension .json si elle existe
            })
          ).unwrap();
          console.log('Données chargées avec succès');
        } catch (error) {
          console.error('Erreur lors du chargement des données:', error);
        }
      }
    };

    loadData();
  }, [dispatch, folderName, filename, jsonLoading]);

  // Ne plus utiliser l'effet de sauvegarde automatique
  // pour éviter les boucles infinies et les sauvegardes inutiles

  // Références pour stocker les instances Sortable afin de pouvoir les détruire
  const sortableLeftRef = useRef(null);
  const sortableRightRef = useRef(null);

  // Cette ligne permet de créer une instance Sortable pour chaque liste
  // de gauche et de droite, permettant ainsi de glisser-déposer les éléments.
  // On dépend de availableSteps.length et pathSteps.length pour recréer
  // les instances quand React reconstruit les listes DOM.
  useEffect(() => {
    if (!leftRef.current || !rightRef.current) return;

    // Détruire les instances précédentes avant que React ne reconstruit le DOM
    if (sortableLeftRef.current) {
      sortableLeftRef.current.destroy();
      sortableLeftRef.current = null;
    }
    if (sortableRightRef.current) {
      sortableRightRef.current.destroy();
      sortableRightRef.current = null;
    }

    sortableLeftRef.current = Sortable.create(leftRef.current, {
      group: {
        name: "shared",
        pull: "clone",
        put: false,
      },
      sort: false,
      animation: 150,
    });

    // Cette instance permet de glisser-déposer les éléments de la liste de droite
    sortableRightRef.current = Sortable.create(rightRef.current, {
      group: {
        name: "shared",
        pull: false,
        put: true,
      },
      animation: 150,
      // Gérer l'ajout d'éléments depuis la liste de gauche :
      // On remet immédiatement l'élément déplacé par Sortable dans son parent
      // pour que React reste maître du DOM, puis on met à jour le state.
      onAdd: function (evt) {
        // Annuler la manipulation DOM de Sortable de façon synchrone
        // AVANT que React ne commence son cycle de rendu
        if (evt.item && evt.item.parentNode) {
          evt.item.parentNode.removeChild(evt.item);
        }
        const stepId = Number(evt.item.dataset.stepid);
        if (!isNaN(stepId)) {
          setPathSteps((prev) => prev.includes(stepId) ? prev : [...prev, stepId]);
        }
      },
      // Gérer la réorganisation des éléments dans la liste de droite
      onUpdate: function (evt) {
        console.log('Réorganisation détectée dans la liste de droite');
        // Récupérer tous les éléments de la liste de droite dans leur nouvel ordre
        const newOrder = Array.from(rightRef.current.children).map(item => {
          return Number(item.dataset.stepid);
        }).filter(id => !isNaN(id));

        console.log('Nouvel ordre après réorganisation:', newOrder);
        // Mettre à jour pathSteps avec le nouvel ordre
        setPathSteps(newOrder);
      },
    });

    return () => {
      if (sortableLeftRef.current) {
        sortableLeftRef.current.destroy();
        sortableLeftRef.current = null;
      }
      if (sortableRightRef.current) {
        sortableRightRef.current.destroy();
        sortableRightRef.current = null;
      }
    };
  // Les dépendances incluent sortableContainerKey : quand les steps changent,
  // la key du div change → React remonte les <ul> → useEffect cleanup détruit
  // l'ancien Sortable → useEffect recrée Sortable sur les nouveaux nœuds DOM.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortableContainerKey]);

  const handleChange = (name, value) => {
    setStageData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    const selectedStage = stages[index];
    setStageData(selectedStage);
    // On split sur les points pour récupérer les IDs (format "11.12.13")
    setPathSteps(selectedStage.stagePath ? selectedStage.stagePath.split('.').map(Number).filter(Boolean) : []);
  };

  const handleDelete = async (index) => {
    if (
      window.confirm(
        `Êtes-vous sûr de vouloir supprimer la procédure "${stages[index].stageTitle}" ?`
      )
    ) {
      try {
        const newStages = stages.filter((_, i) => i !== index);
        const updatedJson = {
          ...jsonData,
          process: { ...jsonData.process, stages: newStages },
        };
        dispatch({ type: "json/updateJsonData", payload: updatedJson });
        await dispatch(saveJsonToBack(updatedJson));
        setMessage({ type: "success", text: "Procédure supprimée avec succès." });
      } catch (error) {
        setMessage({ type: "error", text: error.message });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Créer une copie profonde des données existantes
      const existingData = JSON.parse(JSON.stringify(jsonData || {}));

      // S'assurer que la structure de base existe
      if (!existingData.process) {
        existingData.process = {};
      }

      // Préserver toutes les propriétés existantes du process
      const updatedProcess = {
        ...existingData.process,
        // S'assurer que steps existe et est un tableau
        steps: Array.isArray(existingData.process.steps) ? existingData.process.steps : [],
        // S'assurer que stages existe et est un tableau
        stages: Array.isArray(existingData.process.stages) ? [...existingData.process.stages] : []
      };

      // Préparer le nouveau stage
      // On veut uniquement les IDs séparés par des points
      console.log('pathSteps avant sauvegarde:', pathSteps);
      const newStagePath = pathSteps.filter(id => typeof id === 'number' && !isNaN(id)).join('.');
      console.log('newStagePath calculé:', newStagePath);

      // Synchroniser le state local pour cohérence UI (optionnel mais propre)
      if (stageData.stagePath !== newStagePath) {
        setStageData(prev => ({ ...prev, stagePath: newStagePath }));
      }

      // On force la valeur du champ stagePath pour la sauvegarde
      const newStage = {
        ...stageData,
        stagePath: newStagePath
      };

      console.log('Stage à sauvegarder avec stagePath:', newStage.stagePath);

      // Mettre à jour ou ajouter le stage
      if (editingIndex !== null && updatedProcess.stages[editingIndex]) {
        // Mise à jour d'un stage existant - FORCE le stagePath explicitement
        console.log('Avant mise à jour - Stage existant:', updatedProcess.stages[editingIndex]);
        updatedProcess.stages[editingIndex] = newStage;
        console.log('Après mise à jour - Stage modifié:', updatedProcess.stages[editingIndex]);
      } else {
        // Ajout d'un nouveau stage avec ID unique
        const nextStageID = (updatedProcess.stages.length > 0)
          ? Math.max(...updatedProcess.stages.map(s => Number(s.stageID) || 0)) + 1
          : 1;
        newStage.stageID = nextStageID;
        updatedProcess.stages.push(newStage);
        console.log('Nouveau stage ajouté:', newStage);
      }

      // Mettre à jour les données avec la structure complète
      const updatedData = {
        ...existingData,
        process: updatedProcess,
        // S'assurer que les settings existent
        settings: existingData.settings || {}
      };


      console.log('Données mises à jour avant sauvegarde:', updatedData);

      // Mettre à jour le store Redux
      dispatch({ type: "json/updateJsonData", payload: updatedData });
      // Sauvegarder les données
      await dispatch(saveJsonToBack(updatedData));

      setMessage({
        type: "success",
        text: editingIndex !== null ? "Stage modifié." : "Stage ajouté.",
      });
      setStageData({ stageID: "", stageTitle: "", stageDesc: "", stageRequiresStageID: "", stagePath: "" });
      setPathSteps([]);
      setEditingIndex(null);
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
    }
    setTimeout(() => {
      const anchor = document.getElementById("stagesList");
      if (anchor) {
        const yOffset = -80;
        const y = anchor.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 100);

  };

  const handleCancel = () => {
    setStageData({ stageTitle: "", stageName: "", stageDesc: "", stageRequiresStageID: "", stagePath: "" });
    setPathSteps([]);
    setEditingIndex(null);
  };

  return (
    <div className="form-container p-4">
      {message && (
        <div
          className={`alert alert-${message.type === "success" ? "success" : "danger"
            } alert-dismissible fade show`}
          role="alert"
        >
          {message.text}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage(null)}
            aria-label="Close"
          ></button>
        </div>
      )}
      {/* Tableau des stages */}
      <a id="stagesList"></a>
      <h3 className="text-primary text-center">Liste des procédures</h3>
      {/*<p className="col-md-6 text-center small mx-auto"> description </p>*/}

      <div className="card shadow-sm mb-4 border border-primary" style={{ maxHeight: "320px", overflowY: "scroll" }}>
        <table className="table">
          <thead className="table-light">
            <tr>
              <th className="text-tertiary" style={{ width: "50px" }}>ID</th>
              <th className="text-tertiary" style={{ width: "200px" }}>Nom</th>
              <th className="text-tertiary">Description</th>
              <th className="text-tertiary"  style={{ width: "200px" }}
              title="Pour la réalisation de cette procédure, 
                il faut préalablement avoir réalisé la procédure...">Procédure Requise ℹ️</th>
              <th className="text-tertiary" style={{ width: "100px" }}>Chemin</th>
              <th className="text-tertiary" style={{ width: "120px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(stages) && stages.length > 0 ? (
              stages.map((stage, index) => (
                <tr key={index}>
                  <td>{stage.stageID}</td>
                  <td className="fw-bold">{stage.stageTitle}</td>
                  <td>{stage.stageDesc}</td>
                  {/*<td>{stage.stageRequiresStageID}</td> */}
                  <td alt="stage requires ID">
                    {stage.stageRequiresStageID
                      ? (() => {
                        const requiredStage = stages.find(
                          s => String(s.stageID) === String(stage.stageRequiresStageID)
                        );
                        return requiredStage
                          ? `${stage.stageRequiresStageID}- ${requiredStage.stageTitle}`
                          : stage.stageRequiresStageID;
                      })()
                      : ""}
                  </td>
                  <td>{stage.stagePath}</td>
                  <td alt="Actions sur la procédure">
                    <button
                      className="btn btn-sm btn-outline-primary mx-1"
                      onClick={() => {
                        handleEdit(index);
                        setTimeout(() => {
                          const anchor = document.getElementById("stageEdit");
                          if (anchor) {
                            const yOffset = 15;
                            const y = anchor.getBoundingClientRect().top + window.pageYOffset + yOffset;
                            window.scrollTo({ top: y, behavior: "smooth" });
                          }
                        }, 100);
                      }}
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger mx-1"
                      onClick={() => handleDelete(index)}
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4}>Aucune procédure</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Formulaire d'ajout/édition */}
      <a id="stageEdit" style={{ marginTop: "-80px", paddingTop: "80px", display: "block" }}></a>
      <div className="form-container p-4">
        <Title
          text={
            editingIndex !== null
              ? `Modifier la procédure ${stageData.stageTitle ? ' "' + stageData.stageTitle + '"' : ''}`
              : "Créer une nouvelle procédure"
          }
          className="text-center text-primary mb-4"
        />
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div alt="Nom de la procédure" className="col-md-4">
              <TextInput
                label="Nom de la procédure"
                placeholder="Exemple : Procédure 1"
                value={stageData.stageTitle}
                onChange={(e) =>
                  handleChange("stageTitle", e.target.value)
                }
                className="mb-3"
              />
            </div>
            <div className="col-md-5" alt="Description de la procédure">
              <TextArea
                label="Description de la procédure"
                placeholder="Details de la procédure"
                value={stageData.stageDesc}
                onChange={(e) =>
                  handleChange("stageDesc", e.target.value)
                }
              />
            </div>
            <div alt="ID de la procédure requise" className="col-md-3" title="Quelle procédure doit être réalisée avant de procéder à celle-ci ?">
              <label className="form-label">Préc. procédure requise ℹ️</label>
              <select
                className="form-select mb-4"
                value={stageData.stageRequiresStageID}
                onChange={e => handleChange("stageRequiresStageID", e.target.value)}
              >
                <option value="">Aucune</option>
                {stages.map((stage, idx) => (
                  // Optionnel : ne pas proposer le stage en cours d'édition comme dépendance de lui-même
                  editingIndex !== idx && (
                    <option key={stage.stageID} value={stage.stageID}>
                      {stage.stageTitle || `Procédure ${stage.stageID}`}
                    </option>
                  )
                ))}
              </select>
            </div>
          </div>
          <div className="mt-2 p-4 border-primary rounded" style={{ borderStyle: 'dashed', borderWidth: '1px' }}>
            <h3 className="text-primary text-center">Contenu de la procédure</h3>
            <p className="col-md-6 text-center small mx-auto mb-4">
              Organisez votre procédure à partir de la liste des étapes disponibles<br />
              Glissez-déposez les vers la colonne de chemin et ordonnez-les.
            </p>
            {/* Drag and Drop en ligne */}
            {/* Le key sur ce div force un démontage/remontage complet quand les steps
                disponibles changent, évitant tout conflit entre Sortable et le DOM React. */}
            <div key={sortableContainerKey} alt="Contenu DragNdrop" className="row">
              <div className="col-md-6">
                <label className="form-label">Étapes disponibles</label>
                <ul ref={leftRef} className="list-group border" style={{ height: "200px", overflowY: "auto" }}>
                  {availableSteps.map((step, index) => (
                    <li key={step.id} className="list-group-item" data-stepid={step.id}>
                      <span className="badge bg-secondary me-2"># {step.id}</span>
                      {step.title}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="col-md-6">
                <label className="form-label">Chemin de la procédure (glisser ici)</label>
                <ul ref={rightRef} className="list-group border border-primary" style={{ height: "200px", overflowY: "auto" }}>
                  {pathSteps.map((stepId, idx) => {
                    const step = availableSteps.find(s => s.id === stepId);
                    return (
                      <li key={stepId} className="list-group-item d-flex justify-content-between align-items-center" data-stepid={stepId}>
                        <span>
                          <span className="badge bg-primary me-2"># {stepId}</span>
                          {step ? (step.title || `ID: ${stepId}`) : `ID: ${stepId}`}
                        </span>
                        <button
                          className="btn btn-sm btn-outline-danger mx-1"
                          onClick={() => handleRemoveStepFromPath(stepId)}
                        >
                          <FaTrash />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>

          {/* Boutons */}
          <div alt="Boutons Annuler/Enregistrer" className="d-flex justify-content-center gap-3 mt-4">
            <Button
              text="Annuler"
              className="btn btn-lg btn-outline-secondary"
              onClick={() => {
                handleCancel();
                setTimeout(() => {
                  const anchor = document.getElementById("stagesList");
                  if (anchor) {
                    const yOffset = -80;
                    const y = anchor.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: "smooth" });
                  }
                }, 100);
              }}
              type="button"
            />
            <Button
              text={!stageData.stageTitle
                  ? "⚠️ Nom de la procédure manquant ⚠️"
                  : editingIndex !== null
                    ? `Enregistrer la procédure "${stageData.stageTitle}"`
                    : `Créer la procédure "${stageData.stageTitle}"`
              }
              type="submit"
              className="btn btn-lg btn-outline-primary"
              disabled={
                stageData.stageTitle.trim() === ""
              }
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default StagesManage;

//Creators : Florian Cohen-Joly @3DDGAMES -- Under license